import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
    id: string;
    message: string;
    type: NotificationType;
}

interface NotificationContextType {
    showNotification: (message: string, type?: NotificationType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const showNotification = useCallback((message: string, type: NotificationType = 'info') => {
        const id = Math.random().toString(36).substring(2, 9);
        setNotifications(prev => [...prev, { id, message, type }]);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    }, []);

    const removeNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            {children}
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] flex flex-col items-center pointer-events-none space-y-3 w-full max-w-[90vw] sm:max-w-md">
                {notifications.map((n) => (
                    <Toast
                        key={n.id}
                        message={n.message}
                        type={n.type}
                        onClose={() => removeNotification(n.id)}
                    />
                ))}
            </div>
        </NotificationContext.Provider>
    );
};

const Toast: React.FC<{ message: string; type: NotificationType; onClose: () => void }> = ({ message, type, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Small delay to trigger entry animation
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const icons = {
        success: <CheckCircle className="text-emerald-400" size={20} />,
        error: <AlertCircle className="text-rose-400" size={20} />,
        warning: <AlertTriangle className="text-amber-400" size={20} />,
        info: <Info className="text-blue-400" size={20} />,
    };

    const colors = {
        success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-50',
        error: 'border-rose-500/30 bg-rose-500/10 text-rose-50',
        warning: 'border-amber-500/30 bg-amber-500/10 text-amber-50',
        info: 'border-blue-500/30 bg-blue-500/10 text-blue-50',
    };

    return (
        <div
            className={`
        flex items-center gap-3 px-4 py-3 rounded-2xl border backdrop-blur-xl shadow-2xl pointer-events-auto
        transition-all duration-500 ease-out transform
        ${isVisible ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-8 opacity-0 scale-95'}
        ${colors[type]}
      `}
            style={{
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }}
        >
            <div className="shrink-0">{icons[type]}</div>
            <p className="text-sm font-bold flex-1 leading-tight text-right">{message}</p>
            <button
                onClick={onClose}
                className="shrink-0 p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
                <X size={16} className="opacity-50 hover:opacity-100" />
            </button>
        </div>
    );
};
